import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import { createSelector } from "reselect";
import { useRedux } from "hooks";
import { Utils } from "helpers/utils";
import { APIClient } from "api/apiCore";
import * as urls from "api/urls";
import { setActivePostId2 } from "../../../redux/workplace/wpActions";
import { setApprovalPendingCount } from "../../../redux/layout/actions";
import { WpModes } from "constants/index";
import { WpSliceData } from "../../../redux/workplace/wpTypes";
import LoadingIconWithText from "components/LoadingIconWithText";
import AppSimpleBar from "components/AppSimpleBar";

interface ApprovalListItem {
  taskId: string;
  taskTitle: string;
  approveStatus: string | null;
  myAction: string | null;
  ownerAbbrName: string | null;
  ownerAbbrFore: string | null;
  ownerAbbrBack: string | null;
  pendingApproverName: string | null;
}

const ApprovalList = () => {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.WpSlice,
    (state: any) => state.Layout,
    (wp: WpSliceData, layout) => ({
      activePostId: wp.activePostInfo.postId,
      approvalListChangedCount: layout.approvalListChangedCount,
    })
  );
  const { activePostId, approvalListChangedCount } = useAppSelector(storeData);

  const [items, setItems] = useState<ApprovalListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [onlyMyPending, setOnlyMyPending] = useState<boolean>(true);
  const [filterOpen, setFilterOpen] = useState<boolean>(true);
  const [filterFinished, setFilterFinished] = useState<boolean>(true);
  const [filterNotApproved, setFilterNotApproved] = useState<boolean>(false);

  const loadList = () => {
    setIsLoading(true);
    new APIClient().fnPostForm(urls.WP_TK_LOAD_MY_APPROVAL_LIST, {
      titleSearch: searchText.trim() || undefined,
      onlyPending: onlyMyPending,
      filterOpen: filterOpen,
      filterFinished: filterFinished,
      filterNotApproved: filterNotApproved,
    })
      .then(rs => {
        setItems(rs?.items ?? []);
        // Always the true total (server computes this independent of the filters above) - the
        // sidebar badge must never reflect just what's currently visible under the active filter.
        dispatch(setApprovalPendingCount(Number(rs?.totalPendingCount ?? 0)));
        setIsLoading(false);
      })
      .catch(e => {
        setIsLoading(false);
        Utils.HandleException(e, true);
      });
  }

  // Filters are applied server-side (the list may be paginated/capped in the future, so filtering
  // only what's already been fetched client-side would silently miss matches) - debounce so typing
  // in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      loadList();
    }, 600);
    return () => clearTimeout(timer);
  }, [searchText, onlyMyPending, filterOpen, filterFinished, filterNotApproved, approvalListChangedCount]);

  const onClickItem = (item: ApprovalListItem) => {
    // This task won't be in the approver's own titlesTk (they're not a member), so guard against
    // WorkplaceTitle.tsx's clearActivePostOnTitleChange clearing activePostId right back out from
    // under us the moment titlesTk next reloads (same mechanism notificationRouting.ts uses).
    Utils.SetRedirectPostId(WpModes.Task, item.taskId);
    dispatch(setActivePostId2(WpModes.Task, item.taskId, null, null, null, null, "Approval Center click"));
  }

  return (
    <div className="workplace-title-ctn approval-list-ctn">
      <div className="workplace-title-ctn-head">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <h5 style={{ marginBottom: 0 }}>Approval</h5>
          <div className="workplace-filter-icon-bar">
            {isLoading ? (<LoadingIconWithText loadingText={""} />) : (<i className="fas fa-redo-alt hand fz-11" onClick={loadList}></i>)}
          </div>
        </div>
      </div>

      <div className="px-2 pb-2" style={{ borderBottom: "dashed 1px var(--m2-line-divider-color)" }}>
        <input
          type="text"
          className="form-control form-control-sm mb-2"
          placeholder="ค้นหาชื่องาน"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="d-flex align-items-center flex-wrap" style={{ gap: "4px 12px" }}>
          <div className="form-check approval-filter-mypending">
            <input
              className="form-check-input"
              type="checkbox"
              id="chkApprovalMyPending"
              checked={onlyMyPending}
              onChange={(e) => setOnlyMyPending(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chkApprovalMyPending">รอฉันอนุมัติ</label>
          </div>
          <span className="text-secondary" style={{ whiteSpace: "nowrap" }}>สถานะ:</span>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="chkApprovalStatusOpen"
              checked={filterOpen}
              onChange={(e) => setFilterOpen(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chkApprovalStatusOpen">รออนุมัติ</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="chkApprovalStatusFinished"
              checked={filterFinished}
              onChange={(e) => setFilterFinished(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chkApprovalStatusFinished">อนุมัติแล้ว</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="chkApprovalStatusNotApproved"
              checked={filterNotApproved}
              onChange={(e) => setFilterNotApproved(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chkApprovalStatusNotApproved">ไม่อนุมัติ</label>
          </div>
        </div>
      </div>

      <AppSimpleBar className="workplace-title-ctn-list">
        <ul className="list-unstyled chat-list chat-user-list pe-2">
          {items.map((item, key) => (
            <li key={key} className={classnames({ active: activePostId == item.taskId })} onClick={() => onClickItem(item)}>
              <Link to="#" style={{ paddingLeft: "8px", paddingRight: "10px" }}>
                <div className="d-flex align-items-center" style={{ paddingTop: "8px", paddingBottom: "8px" }}>
                  <div className="chat-user-img align-self-center me-2 ms-0">
                    {(item.ownerAbbrName ?? "").includes('/') ? (
                      <img src={item.ownerAbbrName ?? ""} className="img-fluid rounded-circle img-fit img-xs" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/icons/user-dummy-img.jpg'; }} />
                    ) : (
                      <span className="avatar-wp-title rounded-circle text-uppercase" style={{ backgroundColor: item.ownerAbbrBack ?? undefined, color: item.ownerAbbrFore ?? undefined }}>
                        {item.ownerAbbrName}
                      </span>
                    )}
                  </div>
                  <div className="overflow-hidden">
                    <p className="mb-0 wp-title text-truncate">{item.taskTitle}</p>
                    <p className="text-truncate mb-0 last-chat-text">
                      <i className={Utils.GetDSignApproveStatusIconClass(item.approveStatus)} title={Utils.GetDSignApproveStatusText(item.approveStatus)}></i>
                      <span className="mx-2">•</span>
                      <span className="text-secondary">คุณ:</span>&nbsp;
                      <i className={Utils.GetDSignActionIconClass(item.myAction ?? "")}></i>&nbsp;{Utils.GetDSignActionText(item.myAction)}
                      {item.pendingApproverName && item.myAction !== "Pending" && (
                        <>
                          <span className="mx-1">•</span>
                          <span className="text-secondary">รอ:</span>&nbsp;
                          <i className={Utils.GetDSignActionIconClass("Pending")}></i>&nbsp;{item.pendingApproverName}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
          {!isLoading && items.length === 0 && (
            <li className="text-center text-secondary p-3" style={{ cursor: "default" }}>ไม่มีรายการ</li>
          )}
        </ul>
      </AppSimpleBar>
    </div>
  );
};

export default ApprovalList;
